import { useCallback, useEffect, useRef } from 'react';

import classnames from 'classnames';

import type { Preference, SubscriberPreferenceOption } from '@/types';

import { checkboxStyle, fieldGroupStyle, labelStyle, optionWrapperStyle } from './AdditionalFields.css';

interface Props {
  option: SubscriberPreferenceOption;
  preferenceId: string;
  nameOverride?: string;
  onChange?: () => void;
}

const MultipleChoiceInput = ({ option, preferenceId, nameOverride, onChange }: Props) => {
  return (
    <div className={classnames(optionWrapperStyle)}>
      <input
        id={option.id}
        name={nameOverride || `field_${preferenceId}_option_${option.id}`}
        value={option.value}
        className={classnames(checkboxStyle)}
        type="checkbox"
        defaultChecked={false}
        onChange={onChange}
      />
      <label htmlFor={option.id} className={classnames(labelStyle)}>
        {option.value}
      </label>
    </div>
  );
};

interface MultipleChoiceProps {
  preference: Preference;
}

const REQUIRED_MESSAGE = 'Please select at least one option.';

const MultipleChoice = ({ preference }: MultipleChoiceProps) => {
  const fieldsetRef = useRef<HTMLFieldSetElement>(null);

  const updateValidity = useCallback(() => {
    if (!preference?.required || !fieldsetRef.current) return;

    const checkboxes = fieldsetRef.current.querySelectorAll<HTMLInputElement>('input[type="checkbox"]');
    const anyChecked = Array.from(checkboxes).some((cb) => cb.checked);
    const message = anyChecked ? '' : REQUIRED_MESSAGE;

    for (const cb of checkboxes) {
      cb.setCustomValidity(message);
    }
  }, [preference?.required]);

  useEffect(() => {
    updateValidity();
  }, [updateValidity]);

  return (
    <fieldset ref={fieldsetRef} className={classnames('flex flex-col gap-2', fieldGroupStyle)}>
      <legend className={classnames('w-full', labelStyle)}>
        {preference?.name}
        {preference?.required && <span>*</span>}
      </legend>
      <div className={classnames('flex flex-col gap-1', fieldGroupStyle)}>
        {preference?.subscriber_preference_options?.map((option: SubscriberPreferenceOption) => {
          return (
            <MultipleChoiceInput
              key={option.id}
              option={option}
              preferenceId={preference?.id}
              nameOverride={preference?.name_override}
              onChange={updateValidity}
            />
          );
        })}
      </div>
    </fieldset>
  );
};

export default MultipleChoice;
